import { createFileRoute, Link } from "@tanstack/react-router";
import { SETTING_ONE_NIGHT_ES } from "@/lib/medical-schema";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { SectionCta } from "@/components/site/SectionCta";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";

export const Route = createFileRoute("/es/bypass-gastrico")({
  head: () => {
    const base = esHead("bypass");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("bypass"),
              ...procedureImageObjects(ES_PAGE_MEDIA["/es/bypass-gastrico"], "es", `${SITE}/es/bypass-gastrico`),
              {
                "@type": "MedicalProcedure",
                "@id": `${SITE}/es/bypass-gastrico#procedimiento`,
                name: "Bypass gástrico Roux-en-Y",
                alternateName: "Roux-en-Y gastric bypass",
                description:
                  "Cirugía laparoscópica que crea una bolsa gástrica pequeña y la conecta directamente al intestino delgado para reducir la ingesta y mejorar el control metabólico.",
                bodyLocation: "estómago",
                howPerformed:
                  "Por laparoscopía se crea una bolsa gástrica pequeña que se conecta directamente al intestino delgado, de modo que los alimentos no pasan por la mayor parte del estómago ni por el primer tramo del intestino." + " " + SETTING_ONE_NIGHT_ES,
                url: `${SITE}/es/bypass-gastrico`,
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsBypass,
});

function EsBypass() {
  return (
    <EsPageShell
      page="bypass"
      title="Bypass gástrico laparoscópico Roux-en-Y en el norte de Virginia"
      intro="El bypass gástrico Roux-en-Y es una de las operaciones de pérdida de peso mejor estudiadas del mundo. Se realiza por laparoscopía y combina un estómago más pequeño con un cambio en el recorrido de los alimentos."
    >
      <MedicalReviewBanner lang="es" />

      <EsSection id="como-funciona" heading="En qué consiste el procedimiento">
        <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-start">
          <div className="space-y-3">
            <p>
              La cirugía de bypass gástrico hace dos cambios: reduce el estómago y modifica el
              recorrido de los alimentos por el aparato digestivo. El Dr. Fitzer opera a través de
              seis incisiones de media pulgada, con instrumentos especializados y diminutos, y sigue
              su trabajo con una pequeña cámara que entra por uno de los orificios.
            </p>
            <h3 className="pt-2 text-[1.05rem] md:text-[1.15rem] font-serif text-foreground">
              Creación de la bolsa gástrica
            </h3>
            <p>
              El Dr. Fitzer usa una grapadora especial para dividir el estómago en dos partes: una
              bolsa pequeña y el estómago remanente. Aunque pequeña, la bolsa es la parte
              importante: tiene aproximadamente el tamaño de un huevo pequeño. El resto del estómago
              — el remanente — también se queda en su lugar. Permanece dentro de usted y sigue sano.
            </p>
            <p>
              La operación se llama bypass gástrico porque hace que los alimentos pasen por alto el
              remanente, que es la mayor parte del estómago. Literalmente, tenemos un «bypass
              gástrico».
            </p>
            <p>
              Aunque los alimentos nunca entran en el estómago remanente, este sigue cumpliendo una
              función en la digestión. Por ejemplo, produce enzimas digestivas importantes que se
              encuentran con los alimentos más adelante. Como el remanente se deja dentro, el bypass
              puede revertirse, aunque es muy poco frecuente que esa necesidad llegue a presentarse.
            </p>
            <h3 className="pt-2 text-[1.05rem] md:text-[1.15rem] font-serif text-foreground">
              Luego se divide el intestino
            </h3>
            <p>
              Durante la cirugía, el Dr. Fitzer usa el mismo dispositivo de grapado para dividir el
              intestino delgado, lo que deja dos extremos libres. Conecta uno directamente a la
              nueva bolsa; a ese extremo lo llamamos asa de Roux. Conecta el otro extremo del
              intestino delgado al lado del asa de Roux, un poco más abajo de la bolsa. Los
              alimentos que comen los pacientes de bypass viajan por el asa de Roux.
            </p>
          </div>

          <ProcedureFigure
            media="bypassDiagramEs"
            lang="es"
            lightbox
            className="flex flex-col items-center md:sticky md:top-4"
            imgClassName="w-full h-auto max-w-md rounded-lg"
            captionClassName="mt-2 text-center text-sm text-foreground/70"
          />
        </div>
      </EsSection>

      <EsSection id="beneficios" heading="Beneficios y efectos metabólicos">
        <p>
          El bypass reduce la cantidad de comida que puede ingerir, disminuye ligeramente la
          absorción de calorías y modifica las señales hormonales entre el intestino y el cerebro.
          Ese efecto metabólico explica por qué es especialmente eficaz para la diabetes tipo 2:
          muchos pacientes mejoran su control de azúcar en sangre antes incluso de perder peso
          significativo.
        </p>
        <p>
          También es la mejor opción para quienes sufren de reflujo gastroesofágico importante,
          porque el procedimiento suele aliviarlo, mientras que la manga gástrica puede
          empeorarlo. Si hay una{" "}
          <Link to="/es/reparacion-de-hernia-hiatal" className="text-brand-deep underline hover:no-underline">hernia hiatal</Link>{" "}
          presente, se repara durante la misma operación, lo que aumenta el beneficio antirreflujo.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="Resultados esperados">
        <p>
          Los pacientes de bypass gástrico suelen perder entre el 70 y el 80 por ciento de su
          exceso de peso en los primeros 12 a 18 meses, con buena durabilidad a largo plazo cuando
          se mantienen los hábitos y el seguimiento. Abajo verá la pérdida del exceso de peso de
          los pacientes de bypass del Dr. Fitzer, desde el primer mes hasta los diez años:
        </p>

        <ProcedureFigure
          media="bypassGraph"
          lang="es"
          lightbox
          sizes="(min-width: 768px) 768px, 100vw"
          className="mt-2"
          imgClassName="block w-full h-auto rounded-lg"
        />

        <p>Los resultados varían de una persona a otra.</p>
      </EsSection>


      <EsSection id="riesgos" heading="Riesgos y cuidados a largo plazo">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Riesgos quirúrgicos: sangrado, infección, coágulos, fuga en una de las conexiones y,
            con el tiempo, obstrucción intestinal o hernia interna.
          </li>
          <li>
            Deficiencias nutricionales: al saltarse parte del intestino se absorben menos hierro,
            calcio, vitamina B12 y vitamina D. Los suplementos y los análisis de sangre anuales
            son obligatorios de por vida.
          </li>
          <li>
            Síndrome de vaciamiento rápido (<em>dumping</em>): los azúcares y las grasas en
            exceso pueden causar náuseas, sudoración y diarrea. Para muchos pacientes funciona
            como un recordatorio útil.
          </li>
          <li>
            Alcohol: después del bypass el alcohol se absorbe más rápido y el riesgo de consumo
            problemático aumenta. Conviene hablarlo abiertamente antes de la cirugía.
          </li>
        </ul>
      </EsSection>

      <EsSection id="comparacion" heading="¿Bypass o manga gástrica?">
        <p>
          No hay una respuesta única. En general, el bypass se prefiere cuando existe diabetes
          tipo 2 de difícil control o reflujo importante; la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          es una operación más sencilla, con menos requisitos nutricionales a largo plazo. El Dr.
          Fitzer revisa su historia clínica y sus metas para recomendarle la opción adecuada.
        </p>
      </EsSection>

      <EsSection id="norte-de-virginia" heading="Bypass gástrico en el norte de Virginia">
        <p>
          El Dr. Fitzer realiza el bypass gástrico laparoscópico en Y de Roux en INOVA Fair Oaks Hospital en Fairfax, normalmente con una estancia de una noche. Su primera visita, los estudios preoperatorios y el seguimiento a largo plazo se hacen en nuestro consultorio de 171 Elden Street en Herndon. Allí atendemos con regularidad a pacientes de Arlington, Alexandria y toda el área de Washington, D.C.
        </p>
        <p>
          ¿Está comparando opciones? Vea <Link to="/es/manga-gastrica" className="text-brand-deep underline hover:no-underline">manga gástrica en el Norte de Virginia</Link>. Si ya tuvo un bypass, vea <Link to="/es/cirugia-de-revision/bypass-gastrico" className="text-brand-deep underline hover:no-underline">revisión del bypass gástrico y TORe en Virginia</Link>.
        </p>
        <p>
          ¿Preguntas sobre el bypass gástrico? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_bypass_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="mas-informacion" heading="Más información en español">
        <p>
          ¿No va a usar el seguro? El{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-bypass-gastrico"
            className="font-medium text-brand-logo hover:underline"
          >
            costo del bypass gástrico con pago propio
          </Link>{" "}
          es un estimado de $23,200, que cubre el honorario del cirujano, un día de
          hospital en INOVA Fair Oaks Hospital, un estimado de anestesia y un año de seguimiento
          de rutina con el Dr. Fitzer.
        </p>
        <p className="flex flex-wrap justify-center gap-3 pt-1">

          <Link
            to="/es/bypass-o-manga-gastrica"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Bypass o manga: comparación
          </Link>
          <Link
            to="/es/cirugia-de-revision"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Cirugía de revisión
          </Link>
          <Link
            to="/es/banda-gastrica"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Banda gástrica
          </Link>
        </p>
      </EsSection>
    </EsPageShell>
  );
}
